import { useCallback, useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { useAppDispatch, useAppSelector, useUrlSearchParams } from '@/hooks'
import { fetchAuthenticated } from '@/utils/fetch'
import { selectUtility } from '@/actions/utilities'
import { useProgramRequirements } from '@/app/hooks/useProgramRequirements'
import { useMultiProgramRouteContext } from '..'
import { useSelectedUtility } from '@/hooks/useSelectedUtility'
import { logEvent } from '@/logging'
import { utilityCollection } from '@/reducers/utilityCollection'
import useApiDispatch from '@/hooks/useApiDispatch/useApiDispatch'
import { ProfileEligibilityRequirements } from '@/types/programs-new/profile-requirements'
import { useSelectedPlanFetcher } from '@/authenticated/hooks/plans'
import { selectPlan } from '@/actions/plans'
import { Utility, Plan } from '@/types'
import useMultiProgramFlowControl from '@/hooks/useMultiProgramFlowControl'

const FormSchema = z.object({
  utility: z.object({ id: z.number(), name: z.string() }).nullish(),
  plan: z.object({ id: z.number(), name: z.string() }).nullish(),
})

export enum EligibilityStatus {
  ELIGIBLE = 'ELIGIBLE',
  INELIGIBLE = 'INELIGIBLE',
  NONE = 'NONE',
}

export function useUtilitySelectionViewModel() {
  const { utilityProgram } = useMultiProgramRouteContext()
  const flow = useMultiProgramFlowControl()
  const dispatch = useAppDispatch()
  const { apiDispatch } = useApiDispatch()
  const user = useAppSelector((state) => state.user.user)
  const utilityRefId = user?.profile.utility_ref_id ?? undefined

  const { data: initialSelectedUtility } = useSelectedUtility()
  const { selectedPlan: defaultPlan } = useSelectedPlanFetcher()
  const [planOptions, setPlanOptions] = useState<Plan[]>(
    initialSelectedUtility?.plan_options ?? [],
  )
  const [fetchingPlans, setFetchingPlans] = useState(false)
  const [selectedPlanId, setSelectedPlanId] = useState<number | undefined>(
    defaultPlan?.id ?? undefined,
  )
  const {
    saveForLater,
    routeToNextStep,
    requirementData,
    hasExternalUtilityConnectionRequirement,
    hasCompletedExternalUtilityConnectionRequirement,
    programRequirements,
  } = useProgramRequirements(utilityProgram)
  const planRequired = requirementData
    .map((req) => req.key)
    .includes(ProfileEligibilityRequirements.RequirePlan)

  const [selectedUtility, setSelectedUtility] = useState<
    { id: number; name: string } | undefined
  >(
    initialSelectedUtility
      ? { id: initialSelectedUtility?.id, name: initialSelectedUtility?.name }
      : undefined,
  )

  const [isSaving, setIsSaving] = useState(false)

  const utilityEligibilityStatus: EligibilityStatus = useMemo(() => {
    if (!selectedUtility) {
      return EligibilityStatus.NONE
    }
    const isSelectedUtilityEligible = utilityProgram.utility_ids.includes(
      selectedUtility.id,
    )
    return isSelectedUtilityEligible
      ? EligibilityStatus.ELIGIBLE
      : EligibilityStatus.INELIGIBLE
  }, [selectedUtility, utilityProgram])

  const planEligibilityStatus: EligibilityStatus = useMemo(() => {
    if (!planRequired || !selectedPlanId || !selectedUtility) {
      return EligibilityStatus.NONE
    }
    const isSelectedPlanEligible =
      utilityProgram.utility_plans.includes(selectedPlanId)
    return isSelectedPlanEligible
      ? EligibilityStatus.ELIGIBLE
      : EligibilityStatus.INELIGIBLE
  }, [selectedUtility, utilityProgram, selectedPlanId, planRequired])

  const [utilities, setUtilities] = useState<Utility[]>([])
  const options = useMemo(
    () => (utilities ?? []).map((u) => ({ id: u.id, name: u.name })),
    [utilities],
  )

  const form = useForm<z.infer<typeof FormSchema>>({
    resolver: zodResolver(FormSchema),
    mode: 'onTouched',
  })

  const utilityChangeHandler = useCallback(
    async (value: { utility?: unknown }) => {
      const utility = value.utility as unknown as
        | { id: number; name: string }
        | undefined
      setSelectedUtility(utility)
      if (utility) {
        setFetchingPlans(true)
        const utilityDataRes = await apiDispatch<Utility>(
          utilityCollection.actions.fetch({ id: utility.id }),
        )
        setFetchingPlans(false)
        setPlanOptions(
          utilityDataRes.ok ? utilityDataRes.data.plan_options : [],
        )
      } else {
        setPlanOptions([])
      }
    },
    [planRequired],
  )

  useEffect(() => {
    const { unsubscribe } = form.watch(utilityChangeHandler)
    return () => unsubscribe()
  }, [form, utilityChangeHandler])

  const [utilitySearchLoading, setUtilitySearchLoading] = useState(false)

  const onQueryChange = async (value: string) => {
    if (value.length === 0) {
      return
    }
    const searchParams = new URLSearchParams([['search', value]])
    setUtilitySearchLoading(true)
    const resp = await fetchAuthenticated(
      `/search-utilities`,
      undefined,
      searchParams,
    )
    if (!resp.ok) {
      setUtilitySearchLoading(false)
      logEvent(
        `${resp.status} response searching for utilities: ${await resp.text()}`,
      )
      return
    }
    setUtilitySearchLoading(false)
    const json = (await resp.json()) as Utility[]
    setUtilities(json.slice(0, 4))
  }

  const onNextClick = async () => {
    if (!selectedUtility?.id) {
      return
    }

    if (planRequired && !selectedPlanId) {
      return
    }

    setIsSaving(true)
    await dispatch(selectUtility({ utility_id: selectedUtility.id }))

    if (selectedPlanId) {
      await dispatch(selectPlan({ plan_id: selectedPlanId }))
    }

    setIsSaving(false)
    routeToNextStep()
  }

  const onSeeOtherPrograms = () => {
    saveForLater()
  }

  const onExternalUtilityConnectButtonClick = async (utility: Utility) => {
    flow.setFlowStartPoint(location.pathname, utilityProgram.id)
    await dispatch(selectUtility({ utility_id: utility.id }))
  }

  const isIneligible = [
    utilityEligibilityStatus,
    planEligibilityStatus,
  ].includes(EligibilityStatus.INELIGIBLE)

  const searchParams = useUrlSearchParams()
  useEffect(() => {
    const utilityConnectSuccess =
      searchParams.get('utility-connect-success') === 'true'
    if (utilityConnectSuccess && programRequirements) {
      routeToNextStep()
    }
  }, [programRequirements])

  return {
    form,
    options,
    initialUtility: selectedUtility,
    utilityEligibilityStatus,
    onQueryChange,
    selectedUtility,
    onNextClick,
    onSeeOtherPrograms,
    planRequired,
    planOptions,
    fetchingPlans,
    selectedPlanId,
    setSelectedPlanId,
    planEligibilityStatus,
    isIneligible,
    isSaving,
    utilityProgram,
    hasExternalUtilityConnectionRequirement,
    hasCompletedExternalUtilityConnectionRequirement,
    utilityRefId,
    onExternalUtilityConnectButtonClick,
    initialSelectedUtility,
    utilitySearchLoading,
  }
}
